/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Button, CircularProgress } from '@material-ui/core';
import { useAlert } from '@orientaction/hooks';
import { getFromLS } from '@orientaction/utils';
import { FloppyDiskIcon } from '@phosphor-icons/react';
import { FC, useEffect, useMemo, useState } from 'react';
import { modifyUser } from '../../../../services/user.service';
import { apiServiceNative } from '../../../../utils/fetchNative';
import CategoryTestsAutocomplete from '../../shared/components/CategoryTestsAutocomplete';
import { CategoryTestOption } from '../../shared/types';
import { mapCategoryTests } from '../../shared/utils';
import { CATEGORIES_UPDATED_MESSAGE } from '../constants';
import { useStyles } from '../styles';

interface MinorTestsTabProps {
  user: any;
  onSaved: (updated?: any) => Promise<void> | void;
}

const serializeIds = (items: CategoryTestOption[]) =>
  items
    .map(item => item.id)
    .sort((a, b) => a - b)
    .join(',');

const MinorTestsTab: FC<MinorTestsTabProps> = ({ user, onSaved }) => {
  const classes = useStyles();
  const alert = useAlert();
  const token = getFromLS('userToken') || '';
  const [catalog, setCatalog] = useState<CategoryTestOption[]>([]);
  const [selected, setSelected] = useState<CategoryTestOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const initialIds = useMemo(
    () => serializeIds(mapCategoryTests(user?.category_tests)),
    [user?.category_tests]
  );
  const dirty = serializeIds(selected) !== initialIds;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await apiServiceNative.get('/api/category-tests');
        if (cancelled) {
          return;
        }
        const nextCatalog = mapCategoryTests(response?.data || response);
        setCatalog(nextCatalog);
        const assignedIds = new Set(mapCategoryTests(user?.category_tests).map(item => item.id));
        setSelected(nextCatalog.filter(item => assignedIds.has(item.id)));
      } catch {
        if (!cancelled) {
          setCatalog([]);
          setSelected([]);
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.category_tests]);

  const handleSave = async () => {
    if (!user?.id || saving || !dirty) {
      return;
    }
    setSaving(true);
    try {
      const payload = selected.map(category => ({ id: category.id }));
      await modifyUser({
        id: user.id,
        token,
        category_tests: payload,
      });
      alert.setOpenAlert(true, CATEGORIES_UPDATED_MESSAGE, 'success');
      await onSaved({ category_tests: selected });
    } catch {
      alert.openSimpleAlert();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.card}>
      <div className={classes.editFormBody}>
        <h2 className={classes.sectionTitle}>Tests mineurs autorisés</h2>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="consultant-minor-tests">
            Catégories
          </label>
          <CategoryTestsAutocomplete
            id="consultant-minor-tests"
            options={catalog}
            value={selected}
            disabled={saving}
            onChange={setSelected}
          />
        </div>
      </div>
      <div className={classes.formFooter}>
        <Button
          type="button"
          className={classes.saveBtn}
          startIcon={<FloppyDiskIcon size={18} />}
          disabled={saving || !dirty}
          onClick={handleSave}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

export default MinorTestsTab;
